/**
 * Step 2 — waiting for admin verification. Admin opens /admin/approvals,
 * confirms the designation, assigns a Role, and clicks Approve. We poll
 * /auth/me every 8s so this page auto-advances the moment that happens.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useAuth, routeForStep } from '../../store/auth';

export default function PendingApproval() {
    const user = useAuth(s => s.user);
    const loadMe = useAuth(s => s.loadMe);
    const nav = useNavigate();

    useEffect(() => {
        const id = setInterval(async () => {
            const u = await loadMe();
            if (u && u.onboardingStep !== 'admin_verify') nav(routeForStep(u));
        }, 8_000);
        return () => clearInterval(id);
    }, [loadMe, nav]);

    return (
        <div className="space-y-6 text-center">
            <div className="h-14 w-14 rounded-full bg-amber-50 text-amber-700 grid place-items-center mx-auto">
                <Clock className="h-7 w-7" />
            </div>
            <div>
                <h1 className="text-2xl font-extrabold">Waiting on admin</h1>
                <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
                    Hi <b>{user?.name?.split(' ')[0]}</b> — your email is verified.
                    An admin will confirm your designation and approve you shortly.
                </p>
            </div>

            <div className="ag-card p-5 text-left max-w-md mx-auto">
                <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-1">Submitted details</div>
                <KV k="Name" v={user?.name} />
                <KV k="Email" v={user?.email} />
                <KV k="Phone" v={user?.phone || '—'} />
                <KV k="Office" v={user?.territory || '—'} />
                <KV k="Designation (proposed)" v={user?.designation || 'not set'} />
            </div>

            <p className="text-[11px] text-ink-500">
                This page refreshes automatically. You can safely close the tab — sign back in any time to check.
            </p>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex justify-between items-baseline py-1.5 border-b border-ink-100 last:border-0">
            <div className="text-xs text-ink-500">{k}</div>
            <div className="text-sm font-medium text-right max-w-[60%] truncate">{v}</div>
        </div>
    );
}
